import type { Home, User } from '@lmc/ts-common'
import { mixpanel } from 'common/services/analytics'
import type { Dict } from 'mixpanel-browser'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'

export enum MixpanelEvents {
  PAGE_VISITED = 'Page visited',
  ROSTERING_REPORT_DOWNLOADED = 'Rostering_Report_Downloaded',
  ROTA_BASED_ACCESS_CHANGED = 'Rota_Based_Access_Changed',
  FAMILY_ROSTERING_SETTINGS_CHANGED = 'Family_Rostering_Settings_Changed',
  ROTA_BUILDER_TAB_VIEWED = 'Rota_Builder_Tab_Viewed',
  START_ROTA_GENERATED = 'Start_Rota_Generated',
  ROTA_FILTERS_APPLIED = 'Rota_Filters_Applied',
  ROTA_PATTERN_EDIT_UPDATE_EVENTS = 'Rota_Pattern_Edit_Update_Events',
  ACCESS_CONTROL_HELP_ARTICLE_CLICKED = 'Access_Control_Help_Article_Clicked',
  ROTA_EVENTS_VIEW_TOGGLED = 'Rota_Events_View_Toggled',
  EMAR_V2_STOCK_RECORD_CREATED = 'EMAR_V2_Stock_Record_Created',
  EMAR_V2_DISCREPANCY_CLOSED = 'EMAR_V2_Discrepancy_Closed',
  EMAR_V2_DISPOSAL_MODAL_OPENED_FROM_DISCONTINUE = 'EMAR_V2_Disposal_Modal_Opened_From_Discontinue',
  EMAR_V2_DISPOSE_LATER_CLICKED_FROM_DISCONTINUE = 'EMAR_V2_Dispose_Later_Clicked_From_Discontinue',
  EMAR_V2_HOMEPAGE_CARD_LINK_CLICKED = 'EMAR_V2_Homepage_Card_Link_Clicked',
  WARNING_NOTIFICATIONS_SET = 'Warning_Notifications_Set',
  RESIDENT_STATUS_UPDATED = 'Resident_Status_Updated',
  REASONABLE_ADJUSTMENT_ADDED = 'Reasonable_Adjustment_Added',
  REASONABLE_ADJUSTMENT_EDITED = 'Reasonable_Adjustment_Edited',
  REASONABLE_ADJUSTMENT_DELETED = 'Reasonable_Adjustment_Deleted',
  SERVICE_RECORD_ADDED = 'Service_Record_Added',
  SERVICE_RECORD_UPDATED = 'Service_Record_Updated',
  SERVICE_RECORD_ARCHIVED = 'Service_Record_Archived',
  SERVICE_RECORD_UNARCHIVED = 'Service_Record_Unarchived',
  DEVICE_RECORD_ADDED = 'Device_Record_Added',
  DEVICE_RECORD_UPDATED = 'Device_Record_Updated',
  DEVICE_RECORD_ARCHIVED = 'Device_Record_Archived',
  DEVICE_RECORD_UNARCHIVED = 'Device_Record_Unarchived'
}

export const sendMixpanelEvent = (
  event: MixpanelEvents,
  props: Dict,
  home: Home,
  user: User
) => {
  const homeGroup = home?.homeGroup ?? 'No home group'
  const homeId = home?.id ?? 'Unknown Home'

  mixpanel.track(event, {
    group: homeGroup,
    homeGroup: homeGroup,
    homeName: home?.name || 'Unknown Home',
    home: homeId,
    userId: user?.id || 'Unknown User',
    userName: user?.name
      ? `${user.name.first} ${user.name.last}`
      : 'Unknown User',
    ...props
  })
}

const useMixpanelEvent = () => {
  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const user = useSelector(
    (state: { global: { user: User } }) => state.global.user
  )

  const sendEvent = useCallback(
    (event: MixpanelEvents, props: Dict) => {
      sendMixpanelEvent(event, props, home, user)
    },
    [home, user]
  )

  return sendEvent
}

export default useMixpanelEvent
